// React access to the merchant's settings (filled by StoreContextBootstrap).
// Components read settings through these hooks — never raw store keys.
import { useEffect, useMemo, useState } from 'react';
import { useRuntimeBrandStore } from '@core/store/runtimeBrandStore';
import { useRuntimeThemeStore } from '@core/store/runtimeThemeStore';
import { isNative } from '@core/platform';
import { normalizeBanner } from './banners';
import { parseEcommerceSettings } from './ecommerceSettings';

const isMobileView = () => isNative() || innerWidth <= 576;
export function useIsMobileView() {
  const [isMobile, setIsMobile] = useState(isMobileView);

  useEffect(() => {
    const update = () => setIsMobile(isMobileView());
    addEventListener('resize', update);
    return () => removeEventListener('resize', update);
  }, []);

  return isMobile;
}

/** Parsed ecommerce_settings (see ./ecommerceSettings.js for the fields). */
export function useEcommerceSettings() {
  const raw = useRuntimeThemeStore((state) => state.ecommerceSettings);
  return useMemo(() => parseEcommerceSettings(raw), [raw]);
}

/** Brand colours, logo and name. */
export function useThemeSettings() {
  const primaryColor = useRuntimeThemeStore((s) => s.primaryColor);
  const secondaryColor = useRuntimeThemeStore((s) => s.secondaryColor);
  const backgroundColor = useRuntimeThemeStore((s) => s.backgroundColor);
  const textColor = useRuntimeThemeStore((s) => s.textColor);
  const logoUrl = useRuntimeBrandStore((s) => s.logoUrl);
  const brandName = useRuntimeBrandStore((s) => s.brandName);
  return { primaryColor, secondaryColor, backgroundColor, textColor, logoUrl, brandName };
}

/** The four raw admin banner lists and their enable flags. */
export function useBanners() {
  const isMobile = useIsMobileView();
  const hero = useRuntimeThemeStore((s) =>
    isMobile && s.appBanners?.length ? s.appBanners : s.banners,
  );
  const sub = useRuntimeThemeStore((s) =>
    isMobile && s.appSubBanners?.length ? s.appSubBanners : s.subBanners,
  );
  const promotion = useRuntimeThemeStore((s) =>
    isMobile && s.appPromotionBanners?.length ? s.appPromotionBanners : s.promotionBanners,
  );
  const subPromotion = useRuntimeThemeStore((s) =>
    isMobile && s.appSubPromotionBanners?.length ? s.appSubPromotionBanners : s.subPromotionBanners,
  );
  const enableHeroBanners = useRuntimeThemeStore((s) => s.enableHeroBanners ?? true);
  const enableSubBanners = useRuntimeThemeStore((s) => s.enableSubBanners ?? true);
  const enablePromotionBanners = useRuntimeThemeStore((s) => s.enablePromotionBanners ?? true);
  const enableSubPromotionBanners = useRuntimeThemeStore(
    (s) => s.enableSubPromotionBanners ?? true,
  );

  return {
    hero,
    sub,
    promotion,
    subPromotion,
    enableHeroBanners,
    enableSubBanners,
    enablePromotionBanners,
    enableSubPromotionBanners,
    enableHero: enableHeroBanners,
    enableSub: enableSubBanners,
    enablePromotion: enablePromotionBanners,
    enableSubPromotion: enableSubPromotionBanners,
    isMobile,
  };
}

/**
 * Normalised slides for one banner list ('hero' | 'sub' | 'promotion' |
 * 'subPromotion'). Image-less banners are dropped unless `requireImage` is false.
 */
export function useBannerSlides(kind, { requireImage = true } = {}) {
  const lists = useBanners();
  const source = lists[kind];
  return useMemo(
    () =>
      (source ?? [])
        .map((banner, index) => normalizeBanner(banner, index))
        .filter((slide) => slide && (!requireImage || slide.image)),
    [source, requireImage],
  );
}
